// src/hooks/useSupportManagement.js

import { useState, useEffect, useRef } from "react";
import consumer from "./consumer"; // Adjust path
import {
  closeSupportTicket,
  submitSupportReview,
  updateSupportTicketStatus
} from "../../../services/supportTicket";
import useSupportTickets from "./useSupportTicket"; // Adjust path
import { toast } from "react-toastify";

export default function useSupportManagement() {
  const [tickets, setTickets] = useState([]);
  const [currentTicketId, setCurrentTicketId] = useState(null);
  const [modalData, setModalData] = useState(null);
  const [message, setMessage] = useState("");
  const [reviewComment, setReviewComment] = useState("");
  const [hoverRating, setHoverRating] = useState(0);


  const [selectedRating, setSelectedRating] = useState(0);

  const channelRef = useRef(null);
  const { getSupportTickets } = useSupportTickets();

  // Fetch tickets initially and refresh
  const refreshTickets = async () => {
    try {
      const response = await getSupportTickets();
      const normalized = response.map((ticket) => ({
        id: ticket.id,
        subject: ticket.title,
        status: ticket.status === "In_progress" ? "Pending" : ticket.status,
        updated_at: new Date(ticket.dateTime),
        created_at: new Date(ticket.createdAt || ticket.dateTime),
        category: ticket.category,
        preview: ticket.preview,
        title: ticket.title,
        reviewRating: ticket.review_and_rating,
        messages: (ticket.fullContent || []).map((msg) => ({
          sender:
            (msg.sender || "").toLowerCase() === "you" ? "user" : "support",
          text: msg.text,
          name: msg.name,
          timestamp: new Date(`${msg.time}, 2025`).toISOString(),
        })),
        review: ticket.review || null,
        priority: ticket.priority || "Medium",
      }));
      setTickets(normalized);
    } catch (error) {
      console.error("Failed to fetch support tickets:", error);
    }
  };

  useEffect(() => {
    refreshTickets();
  }, []);

  // Open modal, set current ticket id and modal data
  const handleOpenModal = (ticket) => {
    setCurrentTicketId(ticket.id);
    const userNameFromMessages = ticket.messages.find((m) => m.name)?.name || "Unknown User";
    const alreadyReviewed = ticket.reviewRating?.rating > 0;

    setModalData({
      username: userNameFromMessages,
      userImg: "/user.png",
      role: "Customer",
      ticket: ticket.id,
      category: ticket.category,
      status: ticket.status,
      title: ticket.title,
      priority: ticket.priority,
      chats: ticket.messages
        .slice()
        .reverse()
        .map((m) => ({
          from: m.sender,
          text: m.text,
          time: new Date(m.timestamp).toLocaleTimeString([], {
            hour: "2-digit",
            minute: "2-digit"
          }),
          date: new Date(m.timestamp).toLocaleDateString([], {
            day: "2-digit",
            month: "short",  // e.g. Jan, Feb
            year: "numeric"
          })
        })),
      reviewRating: {
        rating: alreadyReviewed ? ticket.reviewRating.rating : 0,
        comment: alreadyReviewed ? ticket.reviewRating.comment : "",
      },
    });
    // If reviewed → populate states (read-only mode)
    if (alreadyReviewed) {
      setSelectedRating(ticket.reviewRating.rating);
      setReviewComment(ticket.reviewRating.comment);
      setHoverRating(0);
    } else {
      // No review → reset to default
      setSelectedRating(0);
      setReviewComment("");
      setHoverRating(0);
    }
  };

  // WebSocket subscription to live update messages
  useEffect(() => {
    if (!currentTicketId) return;

    const subscription = consumer.subscriptions.create(
      { channel: "SupportTicketChannel", ticket_id: currentTicketId },
      {
        received(data) {
          if (data.message) {
            //  create notification event
            if (data.sender_type === "InternalUser") {
              consumer.subscriptions.subscriptions.forEach(sub => {
                sub.send({
                  type: "NOTIFICATION",
                  ticket_id: currentTicketId,
                  title: `New reply on ticket #${currentTicketId}`,
                  message: data.message,
                });
              });
            }
            setTickets((prev) =>
              prev.map((ticket) => {
                if (ticket.id !== currentTicketId) return ticket;

                const newMsg = {
                  sender:
                    data.sender_type?.toLowerCase() === "internaluser"
                      ? "support"
                      : "user",
                  text:
                    (data.message?.trim && data.message.trim()) ||
                    "No description provided.",
                  timestamp: new Date().toISOString(),
                };

                const alreadyExists = ticket.messages.some(
                  (m) => m.text === newMsg.text && m.sender === newMsg.sender
                );
                if (alreadyExists) return ticket;

                if (modalData && ticket.id === currentTicketId) {
                  setModalData((prev) => ({
                    ...prev,
                    chats: [
                      {
                        from: newMsg.sender,
                        text: newMsg.text,
                        time: new Date(newMsg.timestamp).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }),
                        date: new Date(newMsg.timestamp).toLocaleDateString([], {
                          day: "2-digit",
                          month: "short",  // e.g. Jan, Feb
                          year: "numeric"
                        }),
                      },
                      ...prev.chats,
                    ],
                  }));
                }

                return {
                  ...ticket,
                  messages: [...ticket.messages, newMsg],
                  updated_at: new Date(),
                };
              })
            );
          }
        },
      }
    );

    channelRef.current = subscription;

    return () => {
      try {
        consumer.subscriptions.remove(subscription);
      } catch (e) { }
    };
  }, [currentTicketId, modalData]);

  // Send a new message through websocket and update state
  const sendMessage = () => {
    if (!message.trim() || !channelRef.current || !currentTicketId) return;

    const msg = {
      sender: "user",
      text: message,
      ticket_id: currentTicketId,
      timestamp: new Date().toISOString(),
    };

    setTickets((prev) =>
      prev.map((ticket) => {
        if (ticket.id !== currentTicketId) return ticket;
        return {
          ...ticket,
          messages: [...ticket.messages, msg],
          updated_at: new Date(),
        };
      })
    );

    setModalData((prev) =>
      prev
        ? {
          ...prev,
          chats: [
            {
              from: "user",
              text: message,
              time: new Date(msg.timestamp).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }),
              date: new Date(msg.timestamp).toLocaleDateString([], {
                day: "2-digit",
                month: "short",  // e.g. Jan, Feb
                year: "numeric"
              }),
            },
            ...prev.chats,
          ],
        }
        : prev
    );

    channelRef.current.send({
      action: "receive",
      ticket_id: currentTicketId,
      message: msg,
    });

    setMessage("");
  };

  // Close ticket API call and refresh
  const handleCloseTicket = async (ticketIdArg) => {
    const ticketId = ticketIdArg || currentTicketId;
    if (!ticketId) return;
    try {
      const response = await closeSupportTicket(ticketId);
      await new Promise((resolve) => setTimeout(resolve, 2000));
      await refreshTickets();
      toast.success(response.message, {
        position: "bottom-right",
        autoClose: 5000,
      });
    } catch (err) {
      console.error("Failed to close ticket", err);
    }
  };

  return {
    tickets,
    currentTicketId,
    modalData,
    message,
    activeTab: undefined, // you can move tab state outside or inside your component, so I keep undefined here
    setMessage,
    setCurrentTicketId,
    setModalData,
    refreshTickets,
    handleOpenModal,
    sendMessage,
    handleCloseTicket,
    setTickets,
    submitSupportReview,
    selectedRating,
    setSelectedRating,
    reviewComment,
    setReviewComment,
    hoverRating,
    setHoverRating,
  };
}
